import React, { useMemo, useState, useEffect } from 'react';
import { MapPinned, Plus, Sparkles, Building2, AlertTriangle, MapPin } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import DataTable from '../DataTable';
import Button from '../common/Button';
import { t } from '../../i18n';

const stageOptions = [
  { value: 'originacion', label: 'Originacion' },
  { value: 'prefactibilidad', label: 'Prefactibilidad' },
  { value: 'permisologia', label: 'Permisologia' },
  { value: 'rtb', label: 'RTB' },
  { value: 'ntp', label: 'NTP' }
];

const buildMapSrc = (coordinates) => {
  if (!coordinates) {
    return 'https://www.openstreetmap.org/export/embed.html?bbox=-83%2C-56%2C-66%2C-17&layer=mapnik';
  }
  const lat = Number(coordinates.lat || 0);
  const lng = Number(coordinates.lng || 0);
  const delta = 2.2;
  const left = lng - delta;
  const right = lng + delta;
  const bottom = lat - delta;
  const top = lat + delta;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${lat}%2C${lng}`;
};

const DevelopmentPortfolio = () => {
  const {
    currentUser,
    users,
    renewablePortfolio,
    renewablePermitAlerts,
    updateRenewableProjectApproval,
    updateRenewableProjectStage,
    getRenewableProjectBlockers,
    createPark
  } = useAppContext();

  const [technologyFilter, setTechnologyFilter] = useState('Todas las tecnologias');
  const [stageFilter, setStageFilter] = useState('all');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [stageMessage, setStageMessage] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({
    name: '',
    technology: 'Solar FV',
    region: '',
    commune: '',
    mw: '',
    managerId: '',
    lat: '',
    lng: ''
  });

  const role = currentUser?.role || '';
  const canCreate = ['super_user', 'office_staff', 'project_manager', 'purchasing_manager'].includes(role);
  const canApprovePm = ['super_user', 'project_manager'].includes(role);
  const canApprovePurchasing = ['super_user', 'purchasing_manager'].includes(role);

  const projects = useMemo(() => {
    return renewablePortfolio.filter((project) => {
      const technologyMatch =
        technologyFilter === 'Todas las tecnologias' || project.technology === technologyFilter;
      const statusMatch = stageFilter === 'all' || project.stage === stageFilter;
      return technologyMatch && statusMatch;
    });
  }, [renewablePortfolio, technologyFilter, stageFilter]);

  useEffect(() => {
    if (!selectedProjectId && projects[0]?.id) {
      setSelectedProjectId(projects[0].id);
      return;
    }
    if (selectedProjectId && !projects.find((project) => project.id === selectedProjectId)) {
      setSelectedProjectId(projects[0]?.id || '');
    }
  }, [projects, selectedProjectId]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );

  const totalMw = projects.reduce((sum, item) => sum + Number(item.mw || 0), 0);
  const inPermitting = projects.filter((item) => item.stage === 'permisologia').length;
  const mapSrc = buildMapSrc(selectedProject?.coordinates);
  const selectedBlockers = useMemo(() => {
    if (!selectedProjectId) return [];
    return getRenewableProjectBlockers(selectedProjectId);
  }, [selectedProjectId, getRenewableProjectBlockers]);

  const getProjectAlerts = (projectId) => {
    return renewablePermitAlerts.filter((alert) => alert.projectId === projectId && alert.priority === 'alta')
      .length;
  };

  const columns = [
    {
      key: 'name',
      label: t('Proyecto'),
      sortable: true,
      render: (value) => <span className="font-semibold text-slate-900">{t(value)}</span>
    },
    { key: 'technology', label: t('Tecnologia'), sortable: true },
    {
      key: 'region',
      label: t('Region / Comuna'),
      sortable: true,
      render: (value, row) => `${row.region} / ${row.commune}`
    },
    { key: 'mw', label: 'MW', sortable: true, align: 'right', render: (value) => `${value} MW` },
    {
      key: 'stage',
      label: t('Estado'),
      sortable: true,
      render: (value, row) => (
        <select
          value={value}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            const before = row.stage;
            const next = event.target.value;
            updateRenewableProjectStage(row.id, next);
            if (before !== next) {
              const blockers = getRenewableProjectBlockers(row.id);
              setStageMessage(
                blockers.length > 0
                  ? `Usted no puede avanzar sin esto: ${blockers.join(' ')}`
                  : ''
              );
            }
          }}
          className="px-2 py-1 border border-slate-200 rounded-md text-xs"
        >
          {stageOptions.map((status) => (
            <option key={status.value} value={status.value}>{t(status.label)}</option>
          ))}
        </select>
      )
    },
    {
      key: 'rtbProgress',
      label: t('% Avance RTB'),
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 min-w-[140px]">
          <div className="h-1.5 flex-1 rounded-sm bg-neutral-200 overflow-hidden">
            <div className="h-full bg-brand-600" style={{ width: `${value}%` }} />
          </div>
          <span className="text-xs font-semibold text-slate-700 num">{t(value)}%</span>
        </div>
      )
    },
    { key: 'responsibleName', label: t('Responsable'), sortable: true },
    {
      key: 'approval',
      label: t('Aprobadores'),
      render: (value, row) => (
        <div onClick={(event) => event.stopPropagation()}>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={Boolean(row.approval?.projectManager)}
              onChange={(event) => updateRenewableProjectApproval(row.id, 'projectManager', event.target.checked)}
              disabled={!canApprovePm}
            />{t('Gerente proyecto')}</label>
          <label className="flex items-center gap-2 text-xs text-slate-700 mt-1">
            <input
              type="checkbox"
              checked={Boolean(row.approval?.purchasingManager)}
              onChange={(event) => updateRenewableProjectApproval(row.id, 'purchasingManager', event.target.checked)}
              disabled={!canApprovePurchasing}
            />{t('Gerente compras')}</label>
        </div>
      )
    },
    {
      key: 'procurement',
      label: t('Procurement link'),
      render: (value, row) => (
        <div className="text-xs text-slate-600">
          <p>{t('PR abiertas:')} {t(row.procurement.requisitionsOpen) || 0}</p>
          <p>{t('Licitaciones:')} {t(row.procurement.tendersOpen) || 0}</p>
          <p>{t('PO pendientes:')} {t(row.procurement.purchaseOrdersPendingApproval) || 0}</p>
        </div>
      )
    },
    {
      key: 'alerts',
      label: t('Alertas'),
      align: 'right',
      render: (value, row) => {
        const count = getProjectAlerts(row.id);
        return (
          <span className={`inline-flex items-center gap-1 font-semibold ${count > 0 ? 'text-danger-600' : 'text-neutral-400'}`}>
            <AlertTriangle className="w-4 h-4" />
            {t(count)}
          </span>
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Portafolio de proyectos')}</h1>
          <p className="text-slate-600 text-sm">{t('Gestiona el pipeline renovable desde el arriendo del terreno hasta RTB.')}</p>
          <p className="text-xs text-slate-400 mt-1">{t('Un proyecto puede crearse con información parcial en Desarrollo, pero')} <span className="font-semibold">{t('no se permite emitir PR/OC')}</span> {t(
              'hasta completar aprobación dual (Gerente de Proyecto + Gerente de Compras).'
            )}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
            <Building2 className="w-4 h-4 text-neutral-400" />
            {t(projects.length)} {t('proyectos')}</span>
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
            <Sparkles className="w-4 h-4 text-neutral-400" />
            {totalMw.toFixed(1)} {t('MW')}</span>
          <Button
            variant="primary"
            icon={Plus}
            disabled={!canCreate}
            disabledReason="No tienes permisos para crear proyectos."
            onClick={() => {
              setCreateOpen((prev) => !prev);
              setCreateError('');
            }}
          >{t('Nuevo proyecto')}</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
        <div className="border-b border-slate-200 px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={technologyFilter}
              onChange={(event) => setTechnologyFilter(event.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
            >
              <option>{t('Todas las tecnologias')}</option>
              <option>{t('Solar FV')}</option>
              <option>{t('Eolico')}</option>
            </select>
            <select
              value={stageFilter}
              onChange={(event) => setStageFilter(event.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
            >
              <option value="all">{t('Todos los estados')}</option>
              {stageOptions.map((item) => (
                <option key={item.value} value={item.value}>{t(item.label)}</option>
              ))}
            </select>
          </div>
          <p className="text-sm text-slate-500">{t(projects.length)} {t('proyecto(s) visibles •')} {t(inPermitting)} {t('en permisologia')}</p>
        </div>

        {createOpen && (
          <div className="px-6 py-5 border-b border-slate-200 bg-neutral-50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-slate-900">{t('Crear proyecto (borrador)')}</p>
                <p className="text-xs text-slate-600 mt-1">{t('Completa lo que tengas hoy. Lo que falte se puede ajustar luego en')} <span className="font-semibold">{t('Administración')}</span>{t('. La aprobación dual habilita abastecimiento.')}</p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setCreateOpen(false);
                  setCreateError('');
                }}
              >{t('Cerrar')}</Button>
            </div>

            {createError && (
              <div className="mt-3 rounded-2xl border border-danger-100 bg-danger-50 p-4 text-sm text-danger-700">
                {t(createError)}
              </div>
            )}

            <form
              className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                try {
                  const name = String(createForm.name || '').trim();
                  if (!name) {
                    setCreateError('Debes indicar nombre del proyecto.');
                    return;
                  }
                  const coordinates =
                    createForm.lat && createForm.lng
                      ? { lat: Number(createForm.lat), lng: Number(createForm.lng) }
                      : null;
                  createPark({
                    name,
                    technology: createForm.technology,
                    region: createForm.region,
                    commune: createForm.commune,
                    mw: Number(createForm.mw || 0),
                    managerId: createForm.managerId || null,
                    coordinates
                  });
                  setCreateForm({
                    name: '',
                    technology: 'Solar FV',
                    region: '',
                    commune: '',
                    mw: '',
                    managerId: '',
                    lat: '',
                    lng: ''
                  });
                  setCreateOpen(false);
                  setCreateError('');
                } catch (error) {
                  setCreateError(error?.message || 'No fue posible crear el proyecto.');
                }
              }}
            >
              <input
                value={createForm.name}
                onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                placeholder={t('Nombre proyecto (ej: Parque Solar Atacama 1)')}
                className="md:col-span-2 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                required
              />
              <input
                value={createForm.mw}
                onChange={(e) => setCreateForm((p) => ({ ...p, mw: e.target.value }))}
                placeholder={t('MW (opcional)')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              />
              <input
                value={createForm.region}
                onChange={(e) => setCreateForm((p) => ({ ...p, region: e.target.value }))}
                placeholder={t('Región (opcional)')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              />
              <input
                value={createForm.commune}
                onChange={(e) => setCreateForm((p) => ({ ...p, commune: e.target.value }))}
                placeholder={t('Comuna (opcional)')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              />
              <select
                value={createForm.technology}
                onChange={(e) => setCreateForm((p) => ({ ...p, technology: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              >
                <option value="Solar FV">{t('Solar FV')}</option>
                <option value="Eólica">{t('Eólica')}</option>
                <option value="BESS">{t('BESS')}</option>
              </select>
              <input
                value={createForm.lat}
                onChange={(e) => setCreateForm((p) => ({ ...p, lat: e.target.value }))}
                placeholder={t('Lat (opcional)')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              />
              <input
                value={createForm.lng}
                onChange={(e) => setCreateForm((p) => ({ ...p, lng: e.target.value }))}
                placeholder={t('Lng (opcional)')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              />
              <select
                value={createForm.managerId}
                onChange={(e) => setCreateForm((p) => ({ ...p, managerId: e.target.value }))}
                className="md:col-span-2 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              >
                <option value="">{t('(Opcional) Asignar PM')}</option>
                {(users || []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {t(user.name)} · {t(user.role)}
                  </option>
                ))}
              </select>
              <Button type="submit" variant="primary" className="w-full justify-center md:col-span-3">{t('Crear borrador')}</Button>
            </form>
          </div>
        )}

        {(stageMessage || selectedBlockers.length > 0) && (
          <div className="px-6 py-3 border-b border-warning-100 bg-warning-50 text-warning-700 text-sm">
            {t(stageMessage) || selectedBlockers.join(' ')}
          </div>
        )}

        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <MapPinned className="w-4 h-4 text-neutral-400" />{t('Localizacion fisica de proyectos')}</h2>
              <p className="text-sm text-slate-500">{t(
                'Selecciona un proyecto abajo del mapa para centrar su ubicacion geografica.'
              )}</p>
            </div>
            <span className="text-xs uppercase tracking-wider text-slate-400">{t(projects.length)} {t('con coordenadas')}</span>
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-200 h-[340px]">
            <iframe
              title={t('Mapa de proyectos renovables')}
              className="w-full h-full"
              src={mapSrc}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {projects.map((project) => (
              <Button
                key={project.id}
                size="sm"
                icon={MapPin}
                variant={selectedProjectId === project.id ? 'primary' : 'secondary'}
                onClick={() => setSelectedProjectId(project.id)}
              >
                {t(project.name)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <DataTable
        data={projects}
        columns={columns}
        searchPlaceholder={t('Buscar proyecto...')}
        selectedRowId={selectedProjectId}
        onRowClick={(row) => setSelectedProjectId(row.id)}
        emptyTitle={t('Sin proyectos')}
        emptyDescription={t('Ajusta los filtros de tecnología o estado para ver proyectos.')}
      />
    </div>
  );
};

export default DevelopmentPortfolio;
